import { useCallback, useEffect, useMemo, useState, useRef } from "react";
import { AgGridReact } from "ag-grid-react";
import type {
	ColDef,
	DefaultMenuItem,
	EditableCallbackParams,
	GetContextMenuItemsParams,
	GridApi,
	MenuItemDef,
} from "ag-grid-community";
import Grid2 from "@mui/material/Grid2";
import type { Form } from "../../models/Form";
import { useFormsContext } from "../../context/FormsContext";
import { Button, IconButton } from "@mui/material";
import { ButtonToShowFormsAssetAndUsers } from "../columns-table/ButtonToShowFormsAssetAndUsers";
import { ButtonToShowFormSectionsTable } from "../columns-table/ButtonToShowFormSectionsTable";
import { ErrorModal } from "@dexteel/mesf-core";
import CustomLoadingOverlay from "../../../../../MMS/components/loaders/LazyLoading/LazyLoading";
import { NewAndEditFormModal } from "./forms-modals/modal-new-and-edit-form";
import FormatListBulletedSharpIcon from "@mui/icons-material/FormatListBulletedSharp";
import { DeleteFormDialog } from "./forms-modals/modal-delete-form";
import { INITIAL_VALUES_FORM } from "../../models/FormInitialValues";
import { FormSectionsTable } from "../formsSectionsTable/formSectionsTable";
import { useSearchFormsSections } from "../../hooks/useSearcFormSections";
import AddBoxIcon from "@mui/icons-material/AddBox";
import { FormAssetsAndUsersTable } from "../formAssetsAndUsersTable/formAssetandUsersTable";
import { FormPreviewer } from "../previewFormInstance/viewer-form-instance";
import { useClone } from "../../hooks/useClone";
import { globalDefaultColDef } from "@setup/ag-grid/mesf-theme";

export const FormsTable = ({
	isLoading,
	searchForms,
	error,
	setError,
}: {
	isLoading: boolean;
	searchForms: Function;
	error: string;
	setError: (err: string) => void;
}) => {
	const [gridApi, setGridApi] = useState<GridApi | null>(null);
	const [isSectionsLoading, setIsSectionsLoading] = useState<boolean>(false);
	const [sectionsError, setSectionsError] = useState<string>("");
	const [columnDefs, setColumnDefs] = useState<ColDef[]>();
	const [newMode, setNewMode] = useState<boolean>(false);
	const [openNewEditDialog, setOpenNewEditDialog] = useState<boolean>(false);
	const searchFormSections = useSearchFormsSections({
		setIsSectionsLoading,
		setSectionsError,
	});
	const [openFormAssetAndUsersTable, setOpenFormAssetAndUsersTable] =
		useState(false);

	const [openDeleteDialog, setOpenDeleteDialog] = useState(false);
	const [openSectionsTable, setOpenSectionsTable] = useState(false);
	const [openFormPreviewer, setOpenFormPreviewer] = useState(false);
	const [openAddFormAssetAndUserModal, setOpenAddFormAssetAndUserModal] =
		useState(false);

	const {
		state: { FormId, formsList, formSelectedInTable, searchText },
		actions: {
			setFormId,
			setFormName,
			setFormSelectedInTable,
			setFormInstanceSections,
			setFormInstanceQuestions,
		},
	} = useFormsContext();

	const onActionsClick = (
		e: React.MouseEvent<HTMLButtonElement, MouseEvent>,
		params: EditableCallbackParams,
	) => {
		(params.api as any).showContextMenu({
			column: params.column,
			rowNode: params.node,
			undefined,
			mouseEvent: e.nativeEvent,
		});
	};

	const { CloneForm } = useClone();

	const handleClone = (formId: number) => {
		CloneForm({ FormId: formId, searchForms });
	};

	const loadingOverlayComponent = useMemo<any>(() => {
		return CustomLoadingOverlay;
	}, []);

	const renderColumns = () => {
		setColumnDefs([]);
		const newColumns: ColDef[] = [];
		newColumns.push({
			field: "FormName",
			headerName: "Form Name",
			sortable: true,
			flex: 3,
			minWidth: 130,
		}),
			newColumns.push({
				field: "IncludesComment",
				headerName: "Includes Comment",
				sortable: false,
				flex: 1,
				minWidth: 130,
				maxWidth: 130,
				valueFormatter: (params) => (params.value ? "Yes" : "No"),
			}),
			newColumns.push({
				field: "LayoutCode",
				headerName: "Layout",
				sortable: false,
				flex: 1,
				minWidth: 50,
				maxWidth: 50,
			}),
			newColumns.push({
				field: "IsEnabled",
				headerName: "Is Enabled",
				sortable: false,
				flex: 1,
				minWidth: 70,
				maxWidth: 70,
				valueFormatter: (params) => (params.value ? "Yes" : "No"),
			}),
			newColumns.push({
				field: "",
				headerName: "Sections / Questions",
				sortable: false,
				flex: 3,
				minWidth: 200,
				maxWidth: 200,
				cellRenderer: (params: EditableCallbackParams) =>
					ButtonToShowFormSectionsTable({ params, setOpenSectionsTable }),
			}),
			newColumns.push({
				field: "AssetAndUsers",
				headerName: "Assets and Users",
				sortable: false,
				flex: 3,
				minWidth: 200,
				maxWidth: 200,
				cellRenderer: (params: EditableCallbackParams) =>
					ButtonToShowFormsAssetAndUsers({
						params,
						setOpenFormAssetAndUsersTable,
					}),
			}),
			newColumns.push({
				headerName: "",
				flex: 1,
				minWidth: 65,
				maxWidth: 65,
				cellClass: "ag-columns",
				wrapText: false,
				autoHeight: false,
				cellRenderer: (params: EditableCallbackParams) => {
					return (
						<Grid2
							container
							style={{
								height: "100%",
								display: "flex",
								justifyContent: "center",
								alignItems: "center",
							}}
						>
							<Grid2
								style={{
									display: "flex",
									justifyContent: "center",
									alignItems: "center",
								}}
								size={{ xs: 12, md: 12 }}
							>
								<Grid2
									container
									spacing={1}
									style={{ justifyContent: "flex-end", minWidth: 30 }}
								>
									<Grid2 size={{ xs: 12, md: 12 }}>
										<Button
											fullWidth
											style={{}}
											onClick={(e) => onActionsClick(e, params)}
										>
											<FormatListBulletedSharpIcon
												style={{ height: "auto" }}
												color="action"
											/>
										</Button>
									</Grid2>
								</Grid2>
							</Grid2>
						</Grid2>
					);
				},
			});
		setColumnDefs(newColumns);
	};

	const getContextMenuItems = useCallback(
		(params: GetContextMenuItemsParams): (DefaultMenuItem | MenuItemDef)[] => {
			const data: Form = params.node?.data;
			const result: (DefaultMenuItem | MenuItemDef)[] = [];
			const onlyNew: (DefaultMenuItem | MenuItemDef)[] = [];

			if (data) {
				result.push({
					name: "Content",
					action: () => {
						setNewMode(false);
						setFormId(data?.FormId);
						setFormName(data?.FormName);
						setFormSelectedInTable(data);
						setOpenSectionsTable(true);
					},
					icon: '<i class="fas fa-table"></i>',
				});
			}

			if (
				data &&
				(data.TotalSections as number) >= 1 &&
				(data.TotalQuestions as number) >= 1
			) {
				result.push({
					name: "Preview",
					action: () => {
						setFormId(null);
						setFormId(data?.FormId);
						setOpenFormPreviewer(true);
					},
					icon: '<i class="fas fa-file-invoice"></i>',
				});
			}

			//New

			result.push({
				name: "New Form",
				action: () => {
					setFormSelectedInTable(INITIAL_VALUES_FORM);
					setNewMode(true);
					setOpenNewEditDialog(true);
					setFormId(null);
					setFormName("");
				},
				icon: '<i class="fas fa-plus"></i>',
			});

			//Edit
			if (data) {
				result.push({
					name: "Edit Form",
					action: () => {
						setNewMode(false);
						setFormSelectedInTable(data);
						setFormId(data?.FormId);
						setFormName(data?.FormName);
						setOpenNewEditDialog(true);
					},
					icon: '<i class="fas fa-edit"></i>',
				});
			}

			//View Form Asset Users table
			if (data) {
				result.push({
					name: "Form / Asset / Users",
					action: () => {
						setNewMode(false);
						setFormId(data?.FormId);
						setFormName(data?.FormName);
						setOpenFormAssetAndUsersTable(true);
					},
					icon: '<i class="fas fa-file-invoice"></i>',
				});
			}

			// Content

			if (data) {
				result.push({
					name: "Duplicate Form",
					action: () => {
						setFormSelectedInTable(data);
						handleClone(data.FormId as number);
					},
					icon: '<i class="fas fa-clone"></i>',
				});
			}

			//Delete
			if (data) {
				result.push({
					name: "Delete",
					action: () => {
						setNewMode(false);
						setOpenDeleteDialog(true);
						setFormSelectedInTable(data);
					},
					icon: '<i class="fas fa-trash"></i>',
				});
			}

			return result ? result : onlyNew;
		},
		[gridApi],
	);

	useEffect(() => {
		renderColumns();
	}, [formsList]);

	// Execute search when searchText changes (from debounced input)
	const isFirstRenderSearch = useRef(true);
	useEffect(() => {
		if (isFirstRenderSearch.current) {
			isFirstRenderSearch.current = false;
			return;
		}
		searchForms();
	}, [searchText]);

	return (
		<Grid2 container justifyContent="center">
			<Grid2
				size={{ xs: 12 }}
				container
				justifyContent="center"
				alignItems="center"
			>
				<Grid2
					style={{ padding: "0 15px", position: "relative" }}
					size={{ md: 12, xs: 12 }}
				>
					<FormAssetsAndUsersTable
						show={openFormAssetAndUsersTable}
						onHide={(shouldUpdate: boolean) => {
							if (shouldUpdate) {
								searchForms();
							}
							setOpenFormAssetAndUsersTable(false);
						}}
						setOpenAddFormAssetAndUserModal={setOpenAddFormAssetAndUserModal}
						openAddFormAssetAndUserModal={openAddFormAssetAndUserModal}
					/>

					<Grid2
						style={{
							height: "65vh",
							paddingBottom: 45,
						}}
						size={{ md: 12, xs: 12 }}
					>
						<AgGridReact
							rowData={formsList}
							columnDefs={columnDefs}
							defaultColDef={globalDefaultColDef}
							rowHeight={38}
							headerHeight={42}
							loadingOverlayComponent={loadingOverlayComponent}
							animateRows={true}
							getContextMenuItems={(e: any) => getContextMenuItems(e)}
							pagination={false}
							onRowDoubleClicked={(event) => {
								setFormSelectedInTable(event.data);
								setFormId(event.data?.FormId);
								setFormName(event.data?.FormName);
								setOpenSectionsTable(true);
							}}
							rowSelection="single"
							onGridReady={(params) => setGridApi(params.api)}
							loading={isLoading}
						/>

						<IconButton
							onClick={() => {
								setFormSelectedInTable(INITIAL_VALUES_FORM);
								setOpenNewEditDialog(true);
								setNewMode(true);
							}}
							aria-label="add"
							color="primary"
							style={{ position: "absolute", padding: 0, bottom: -5, left: 17 }}
						>
							<AddBoxIcon style={{ width: 35, height: 35 }} />
						</IconButton>

						<NewAndEditFormModal
							show={openNewEditDialog}
							data={formSelectedInTable}
							newMode={newMode}
							onHide={(shouldUpdate: boolean) => {
								if (shouldUpdate) {
									searchForms();
								}
								setOpenNewEditDialog(false);
							}}
						/>
						<DeleteFormDialog
							show={openDeleteDialog}
							searchData={searchForms}
							onHide={(shouldUpdate: boolean) => {
								if (shouldUpdate) {
									searchForms();
								}
								setOpenDeleteDialog(false);
							}}
						/>
						<FormSectionsTable
							show={openSectionsTable}
							searchFormSections={searchFormSections}
							searchForms={searchForms}
							isSectionsLoading={isSectionsLoading}
							onHide={(shouldUpdate: boolean) => {
								if (shouldUpdate) {
									searchForms();
									searchFormSections();
								}
								setOpenSectionsTable(false);
							}}
						/>
						<FormPreviewer
							show={openFormPreviewer}
							FormId={FormId as number}
							onHide={(shouldUpdate: boolean) => {
								setOpenFormPreviewer(false);
								setFormId(null);
								setFormInstanceQuestions([]);
								setFormInstanceSections([]);
							}}
						/>
					</Grid2>
				</Grid2>
			</Grid2>
			<ErrorModal error={error} onHide={() => setError("")} />
			<ErrorModal error={sectionsError} onHide={() => setSectionsError("")} />
		</Grid2>
	);
};
